import ImageLightboxPreviewTrigger, {
  type LightboxPreviewMedia,
} from '@/components/media/ImageLightboxPreviewTrigger'
import {
  IndexDataTable,
  getIndexPaginationMeta,
  IndexTablePagination,
  type IndexDataTableProps,
  type IndexDataTableRowPresentation,
  type IndexPaginationResponse,
  type IndexTablePaginationProps,
} from '@/components/tables/IndexTable'
import { Button } from '@/components/ui/button'
import {
  detailDataHeaderClass,
  detailDataListClass,
  detailDataRowClass,
} from '@/components/patterns/detail-page'
import { Typography } from '@/components/ui/typography'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAbility } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { formatCardBrand, formatCardLast4 } from '@/lib/payment-method-display'
import {
  subscriptionFrequencySupportsPreferredWeekday,
  subscriptionPreferredWeekdayOptions,
} from '@/lib/subscription-frequency'
import { cn, formatCurrency, parseCurrency } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import type {
  FlowerSubscription,
  FlowerSubscriptionImage,
  FlowerSubscriptionListItem,
} from '@/lib/services/flower-subscriptions/flower-subscriptions.types'
import {
  AlertTriangle,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  Flower2,
  Repeat2,
  Store,
  UserRound,
  XCircle,
  type LucideIcon,
} from 'lucide-react'
import { memo, useMemo, type ReactNode } from 'react'

type SubscriptionStatusView = Partial<
  Pick<FlowerSubscriptionListItem, 'status' | 'status_label' | 'issue_type' | 'issue_label'>
>
type SubscriptionWeekdayView = Partial<
  Pick<
    FlowerSubscription,
    'frequency' | 'preferred_fulfillment_weekday' | 'preferred_fulfillment_weekday_label'
  >
>
interface SubscriptionPackageView extends Partial<
  Pick<FlowerSubscription, 'id' | 'frequency_label'>
> {
  product?:
    | (Partial<Pick<FlowerSubscription['product'], 'id' | 'name'>> & {
        /** The existing display also supports historical URL-only images. */
        image?: FlowerSubscriptionImage | string | null
      })
    | null
  tier?: Partial<Pick<FlowerSubscription['tier'], 'name'>> | null
  amounts?: Partial<Pick<FlowerSubscription['amounts'], 'item'>> | null
}
type SubscriptionTone = keyof typeof subscriptionTableToneClassName
type SubscriptionTreatment = 'quiet' | 'action'
interface SubscriptionStatusOption {
  label: string
  treatment?: SubscriptionTreatment
  tone?: SubscriptionTone
  icon?: LucideIcon
  variant?: 'warning'
}
interface SubscriptionTableStatusProps {
  children: ReactNode
  tone?: SubscriptionTone
  icon?: LucideIcon
  className?: string
}
type SubscriptionRowPresentation = IndexDataTableRowPresentation &
  Required<Pick<IndexDataTableRowPresentation, 'tone'>>
type SubscriptionDate = Parameters<typeof dayjs>[0]

const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/

export const subscriptionStatusOptions: Partial<
  Record<FlowerSubscription['status'], SubscriptionStatusOption>
> = {
  active: {
    label: 'Active',
    treatment: 'quiet',
    tone: 'success',
    icon: CheckCircle2,
  },
  payment_failed: {
    label: 'Payment Failed',
    treatment: 'action',
    tone: 'destructive',
    icon: AlertTriangle,
  },
  requires_review: { label: 'Fulfillment Unavailable', variant: 'warning' },
  cancelled: {
    label: 'Cancelled',
    treatment: 'quiet',
    tone: 'muted',
    icon: XCircle,
  },
}

const subscriptionTableToneClassName = {
  destructive: {
    action:
      'bg-status-destructive-background text-status-destructive-foreground dark:bg-status-destructive-background dark:text-status-destructive-foreground',
    quiet: 'text-status-destructive-foreground dark:text-status-destructive-foreground',
  },
  muted: {
    action: 'bg-muted/40 text-muted-foreground dark:bg-muted/20',
    quiet: 'text-muted-foreground',
  },
  success: {
    action:
      'bg-status-success-background text-status-success-foreground dark:bg-status-success-background dark:text-status-success-foreground',
    quiet: 'text-status-success-foreground dark:text-status-success-foreground',
  },
  warning: {
    action:
      'bg-status-warning-background text-status-warning-foreground dark:bg-status-warning-background dark:text-status-warning-foreground',
    quiet: 'text-status-warning-foreground dark:text-status-warning-foreground',
  },
}

function getSubscriptionTableToneClassName(
  tone: SubscriptionTone,
  treatment: SubscriptionTreatment
): string {
  return (
    subscriptionTableToneClassName[tone]?.[treatment] ??
    subscriptionTableToneClassName.muted[treatment]
  )
}

function SubscriptionTableIcon({
  icon: Icon,
  className = 'size-3.5',
}: {
  icon?: LucideIcon
  className?: string
}) {
  return Icon ? <Icon className={className} aria-hidden="true" /> : null
}

function SubscriptionTableActionPill({
  children,
  tone = 'warning',
  icon,
  className = '',
}: SubscriptionTableStatusProps) {
  return (
    <span
      data-subscription-table-status="action"
      data-tone={tone}
      className={cn(
        'inline-flex h-7 w-fit shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-sm font-medium leading-none',
        getSubscriptionTableToneClassName(tone, 'action'),
        className
      )}
    >
      <span className="size-1.5 rounded-full bg-current" aria-hidden="true" />
      <SubscriptionTableIcon icon={icon} className="size-3" />
      {children}
    </span>
  )
}

function SubscriptionTableQuietStatus({
  children,
  tone = 'success',
  icon = CheckCircle2,
  className = '',
}: SubscriptionTableStatusProps) {
  return (
    <span
      data-subscription-table-status="quiet"
      data-tone={tone}
      className={cn(
        'inline-flex min-h-7 w-fit shrink-0 items-center gap-1.5 whitespace-nowrap text-sm font-medium',
        getSubscriptionTableToneClassName(tone, 'quiet'),
        className
      )}
    >
      <SubscriptionTableIcon icon={icon} />
      {children}
    </span>
  )
}

export function getSubscriptionName(subscription?: SubscriptionPackageView | null): string {
  return subscription?.product?.name || `Subscription #${subscription?.id ?? ''}`.trim()
}

export function getSubscriptionTierName(subscription?: SubscriptionPackageView | null): string {
  return subscription?.tier?.name || 'Selected tier'
}

export function getSubscriptionFrequencyLabel(
  subscription?: SubscriptionPackageView | null
): string {
  return subscription?.frequency_label || 'Recurring'
}

export function getSubscriptionPreferredWeekdayLabel(
  subscription?: SubscriptionWeekdayView | null
): string | null {
  if (!subscriptionFrequencySupportsPreferredWeekday(subscription?.frequency)) {
    return null
  }

  return (
    subscription?.preferred_fulfillment_weekday_label ||
    subscriptionPreferredWeekdayOptions.find(
      (option) => option.id === subscription?.preferred_fulfillment_weekday
    )?.name ||
    'Not selected'
  )
}

export function formatSubscriptionDate(
  value: SubscriptionDate,
  fallback = 'Not scheduled'
): string {
  if (!value) {
    return fallback
  }

  return (
    typeof value === 'string' && dateOnlyPattern.test(value) ? dayjs.utc(value) : dayjs(value)
  ).format('MMM D, YYYY')
}

export function formatSubscriptionShortDate(value: SubscriptionDate, fallback = '-'): string {
  if (!value) {
    return fallback
  }

  return (
    typeof value === 'string' && dateOnlyPattern.test(value) ? dayjs.utc(value) : dayjs(value)
  ).format('MM/DD/YYYY')
}

export function getSubscriptionImage(subscription?: SubscriptionPackageView | null): string | null {
  const image = subscription?.product?.image

  if (typeof image === 'string') {
    return image
  }

  const url = image?.url ?? image?.src

  return typeof url === 'string' ? url : null
}

export function getSubscriptionImageMedia(
  subscription?: SubscriptionPackageView | null
): LightboxPreviewMedia | null {
  const image = subscription?.product?.image

  if (typeof image === 'string') {
    return { url: image }
  }

  if (image?.url) {
    return image
  }

  if (image?.src) {
    return { ...image, url: image.src }
  }

  return null
}

export function getSubscriptionPaymentMethodLabel(
  subscription?: Partial<Pick<FlowerSubscriptionListItem, 'payment_method'>> | null
): string {
  const paymentMethod = subscription?.payment_method

  if (!paymentMethod?.last4) {
    return 'No card on file'
  }

  const last4 = formatCardLast4(paymentMethod)

  return last4
    ? `${formatCardBrand(paymentMethod)} ending ${last4}`
    : formatCardBrand(paymentMethod)
}

export function hasSubscriptionPaymentProblem(
  subscription?: SubscriptionStatusView | null
): boolean {
  return ['payment_failed', 'requires_review'].includes(subscription?.status ?? '')
}

export function getSubscriptionIssueTone(
  subscription?: SubscriptionStatusView | null
): 'destructive' | 'warning' | null {
  if (subscription?.status === 'payment_failed' || subscription?.issue_type === 'payment_review') {
    return 'destructive'
  }

  return subscription?.status === 'requires_review' ? 'warning' : null
}

function getSubscriptionStatusOption(
  subscription?: SubscriptionStatusView | null
): SubscriptionStatusOption {
  const status = subscription?.status

  if (status === 'requires_review') {
    return {
      label: subscription?.issue_label || subscription?.status_label || 'Review Required',
      treatment: 'action',
      tone: getSubscriptionIssueTone(subscription) ?? 'warning',
      icon: subscription?.issue_type === 'payment_review' ? CreditCard : AlertTriangle,
    }
  }

  return (
    (status ? subscriptionStatusOptions[status] : undefined) ?? {
      label: subscription?.status_label || 'Subscription',
      treatment: 'quiet',
      tone: 'muted',
      icon: Repeat2,
    }
  )
}

export function SubscriptionStatusBadge({
  subscription,
  className,
}: {
  subscription?: SubscriptionStatusView | null
  className?: string
}) {
  const config = getSubscriptionStatusOption(subscription)

  if (config.treatment === 'action' || config.variant === 'warning') {
    return (
      <SubscriptionTableActionPill
        tone={config.tone ?? 'warning'}
        icon={config.icon ?? AlertTriangle}
        className={className}
      >
        {config.label}
      </SubscriptionTableActionPill>
    )
  }

  return (
    <SubscriptionTableQuietStatus
      tone={config.tone ?? 'muted'}
      icon={config.icon ?? Repeat2}
      className={className}
    >
      {config.label}
    </SubscriptionTableQuietStatus>
  )
}

export function SubscriptionSkippedBadge({
  subscription,
}: {
  subscription?: Partial<Pick<FlowerSubscription, 'has_skipped_next_cycle'>> | null
}) {
  if (!subscription?.has_skipped_next_cycle) {
    return null
  }

  return (
    <SubscriptionTableQuietStatus tone="warning" icon={CalendarClock}>
      Next order skipped
    </SubscriptionTableQuietStatus>
  )
}

export function SubscriptionPackageAvatar({
  subscription,
  className,
}: {
  subscription?: SubscriptionPackageView | null
  className?: string
}) {
  const imageUrl = getSubscriptionImage(subscription)
  const name = getSubscriptionName(subscription)

  return (
    <div
      className={cn(
        'flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-muted text-muted-foreground',
        className
      )}
    >
      {imageUrl ? (
        <img src={imageUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <Flower2 className="size-5" aria-label={name} />
      )}
    </div>
  )
}

function SubscriptionPrimaryCell({ subscription }: { subscription: FlowerSubscriptionListItem }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <SubscriptionPackageAvatar subscription={subscription} />
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="truncate font-semibold text-foreground">
            {getSubscriptionName(subscription)}
          </span>
        </div>
        <Typography variant="caption" className="mt-1 block truncate">
          #{subscription.id} · {getSubscriptionTierName(subscription)} ·{' '}
          {getSubscriptionFrequencyLabel(subscription)}
        </Typography>
      </div>
    </div>
  )
}

function SubscriptionCustomerCell({ subscription }: { subscription: FlowerSubscriptionListItem }) {
  const customer = subscription.customer

  return (
    <div className="min-w-0">
      <span className="block truncate font-medium text-foreground">
        {customer?.name || 'No purchaser'}
      </span>
    </div>
  )
}

function SubscriptionOrganizationCell({
  subscription,
}: {
  subscription: FlowerSubscriptionListItem
}) {
  return (
    <span className="block truncate text-foreground">
      {subscription.organization_name || 'Organization'}
    </span>
  )
}

function SubscriptionStatusCell({ subscription }: { subscription: FlowerSubscriptionListItem }) {
  const showSkippedStatus =
    !hasSubscriptionPaymentProblem(subscription) && subscription?.has_skipped_next_cycle

  return (
    <div className="flex min-w-0 flex-wrap justify-start gap-1.5 @[40rem]/index-table:justify-center">
      {showSkippedStatus ? (
        <SubscriptionSkippedBadge subscription={subscription} />
      ) : (
        <SubscriptionStatusBadge subscription={subscription} />
      )}
    </div>
  )
}

function SubscriptionDateCell({
  value,
  fallback = 'Not scheduled',
}: {
  value: SubscriptionDate
  fallback?: string
}) {
  return (
    <span className="whitespace-nowrap font-normal text-muted-foreground tabular-nums">
      {formatSubscriptionShortDate(value, fallback)}
    </span>
  )
}

function SubscriptionPaymentCell({ subscription }: { subscription: FlowerSubscriptionListItem }) {
  return (
    <span className="block truncate text-sm text-muted-foreground">
      {getSubscriptionPaymentMethodLabel(subscription)}
    </span>
  )
}

const subscriptionSortColumns = {
  id: {
    asc: 'id_asc',
    desc: 'id_desc',
    defaultSort: 'id_desc',
  },
  customer: {
    asc: 'customer_asc',
    desc: 'customer_desc',
    defaultSort: 'customer_asc',
  },
  organization: {
    asc: 'organization_asc',
    desc: 'organization_desc',
    defaultSort: 'organization_asc',
  },
  package: {
    asc: 'package_asc',
    desc: 'package_desc',
    defaultSort: 'package_asc',
  },
  status: {
    asc: 'status_asc',
    desc: 'status_desc',
    defaultSort: 'status_asc',
  },
  nextFulfillment: {
    asc: 'next_fulfillment_asc',
    desc: 'next_fulfillment_desc',
    defaultSort: 'next_fulfillment_asc',
  },
  nextCharge: {
    asc: 'next_charge_asc',
    desc: 'next_charge_desc',
    defaultSort: 'next_charge_asc',
  },
}

export function getFlowerSubscriptionColumns({
  showCustomer = true,
  showOrganization = false,
}: {
  showCustomer?: boolean
  showOrganization?: boolean
} = {}): ColumnDef<FlowerSubscriptionListItem>[] {
  const columns: (ColumnDef<FlowerSubscriptionListItem> | null)[] = [
    {
      id: 'package',
      header: 'Subscription',
      cell: ({ row }) => <SubscriptionPrimaryCell subscription={row.original} />,
      meta: {
        label: 'Subscription',
        kind: 'primary',
        minWidth: '18rem',
        preferredWidth: '22rem',
        icon: Repeat2,
        sortable: subscriptionSortColumns.package,
      },
    },
    showOrganization
      ? {
          id: 'organization',
          header: 'Organization',
          cell: ({ row }) => <SubscriptionOrganizationCell subscription={row.original} />,
          meta: {
            label: 'Organization',
            kind: 'primary',
            minWidth: '12rem',
            preferredWidth: '14rem',
            icon: Store,
            sortable: subscriptionSortColumns.organization,
          },
        }
      : null,
    showCustomer
      ? {
          id: 'customer',
          header: 'Purchaser',
          cell: ({ row }) => <SubscriptionCustomerCell subscription={row.original} />,
          meta: {
            label: 'Purchaser',
            kind: 'text',
            minWidth: '12rem',
            preferredWidth: '14rem',
            icon: UserRound,
            sortable: subscriptionSortColumns.customer,
          },
        }
      : null,
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <SubscriptionStatusCell subscription={row.original} />,
      meta: {
        label: 'Status',
        kind: 'badge',
        minWidth: '12rem',
        preferredWidth: '13rem',
        icon: Repeat2,
        sortable: subscriptionSortColumns.status,
        align: 'center',
      },
    },
    {
      id: 'nextFulfillment',
      header: 'Next Fulfillment',
      cell: ({ row }) => <SubscriptionDateCell value={row.original.next_fulfillment_date} />,
      meta: {
        label: 'Next Fulfillment',
        kind: 'date',
        minWidth: '8rem',
        preferredWidth: '9rem',
        icon: CalendarDays,
        sortable: subscriptionSortColumns.nextFulfillment,
        align: 'center',
      },
    },
    {
      id: 'nextCharge',
      header: 'Next Charge',
      cell: ({ row }) => <SubscriptionDateCell value={row.original.next_charge_date} />,
      meta: {
        label: 'Next Charge',
        kind: 'date',
        minWidth: '7.5rem',
        preferredWidth: '8.5rem',
        icon: CalendarClock,
        sortable: subscriptionSortColumns.nextCharge,
        align: 'center',
      },
    },
    {
      id: 'payment',
      header: 'Payment',
      cell: ({ row }) => <SubscriptionPaymentCell subscription={row.original} />,
      meta: {
        label: 'Payment',
        kind: 'text',
        minWidth: '9rem',
        preferredWidth: '11rem',
        icon: CreditCard,
      },
    },
  ]

  return columns.filter((column): column is ColumnDef<FlowerSubscriptionListItem> =>
    Boolean(column)
  )
}

export function getFlowerSubscriptionDetailLink<TLink>(
  path: {
    subscriptions: {
      show: (id: number) => { using: (scope: { orgId: number }) => { link: TLink } }
    }
  },
  subscription: Pick<FlowerSubscriptionListItem, 'id' | 'organization_id'>
): TLink {
  return path.subscriptions.show(subscription.id).using({ orgId: subscription.organization_id })
    .link
}

export function getFlowerSubscriptionRowPresentation(
  subscription?: SubscriptionStatusView | null
): SubscriptionRowPresentation {
  if (hasSubscriptionPaymentProblem(subscription)) {
    return { tone: getSubscriptionIssueTone(subscription) ?? 'warning' }
  }

  if (subscription?.status === 'cancelled') {
    return { tone: 'muted', contentTone: 'muted' }
  }

  return { tone: 'neutral' }
}

const flowerSubscriptionDetailRowClassName: Partial<
  Record<SubscriptionRowPresentation['tone'], string>
> = {
  destructive:
    'bg-status-destructive-background hover:bg-status-destructive-border/45 dark:bg-status-destructive-background/35 dark:hover:bg-status-destructive-background/55',
  warning:
    'bg-status-warning-background hover:bg-status-warning-border/45 dark:bg-status-warning-background/35 dark:hover:bg-status-warning-background/55',
  muted: 'bg-muted/20 text-muted-foreground hover:bg-muted/35 dark:bg-muted/10',
}

function getFlowerSubscriptionDetailRowClassName(
  subscription: SubscriptionStatusView
): string | undefined {
  return flowerSubscriptionDetailRowClassName[
    getFlowerSubscriptionRowPresentation(subscription).tone
  ]
}

type FlowerSubscriptionsIndexTableProps = Pick<
  IndexDataTableProps<FlowerSubscriptionListItem>,
  'sort' | 'toolbar' | 'isLoading' | 'onSortChange'
> &
  Pick<
    IndexTablePaginationProps,
    'pageSize' | 'onPageChange' | 'onPageIntent' | 'onPageSizeChange'
  > & {
    subscriptions: FlowerSubscriptionListItem[]
    response?: IndexPaginationResponse | null
    page: number
    showCustomer?: boolean
  }

export const FlowerSubscriptionsIndexTable = memo(function FlowerSubscriptionsIndexTable({
  subscriptions,
  response,
  page,
  pageSize,
  sort,
  toolbar,
  isLoading,
  onPageChange,
  onPageIntent,
  onPageSizeChange,
  onSortChange,
  showCustomer = true,
}: FlowerSubscriptionsIndexTableProps) {
  const path = usePathHelper()
  const ability = useAbility()
  const isGlobalOrg = useIsGlobalOrg()
  const showOrganization = ability.can('access', 'global') && isGlobalOrg
  const paginationMeta = getIndexPaginationMeta(response, page, pageSize)
  const columns = useMemo(
    () => getFlowerSubscriptionColumns({ showCustomer, showOrganization }),
    [showCustomer, showOrganization]
  )

  return (
    <div className="space-y-4">
      <IndexDataTable
        data={subscriptions}
        columns={columns}
        sort={sort}
        onSortChange={onSortChange}
        rowLink={(subscription) => getFlowerSubscriptionDetailLink(path, subscription)}
        getRowPresentation={getFlowerSubscriptionRowPresentation}
        getRowId={(subscription) => String(subscription.id)}
        toolbar={toolbar}
        isLoading={isLoading}
        emptyState={<Typography variant="supporting">No subscriptions found.</Typography>}
        tableClassName="text-sm"
      />

      <IndexTablePagination
        meta={paginationMeta}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageIntent={onPageIntent}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  )
})

const customerSubscriptionGridClass = 'md:grid-cols-[8.5rem_13.5rem_minmax(0,1fr)_8.5rem_11rem]'

export function CustomerSubscriptionsPanel({
  subscriptions = [],
  isLoading = false,
}: {
  subscriptions?: FlowerSubscriptionListItem[]
  isLoading?: boolean
}) {
  const path = usePathHelper()

  if (isLoading) {
    return (
      <div className="p-5">
        <Typography variant="supporting">Loading subscriptions...</Typography>
      </div>
    )
  }

  if (subscriptions.length === 0) {
    return (
      <div className="p-5">
        <Typography variant="supporting">No subscriptions yet.</Typography>
      </div>
    )
  }

  return (
    <div>
      <div
        className={cn(
          'hidden gap-4 px-4 py-2.5 md:grid',
          detailDataHeaderClass,
          customerSubscriptionGridClass
        )}
      >
        <Typography variant="eyebrow">Subscription</Typography>
        <Typography variant="eyebrow">Status</Typography>
        <Typography variant="eyebrow">Package</Typography>
        <Typography variant="eyebrow" align="right">
          Next
        </Typography>
        <Typography variant="eyebrow" align="right">
          Payment
        </Typography>
      </div>
      <div className={detailDataListClass}>
        {subscriptions.map((subscription) => (
          <div
            key={subscription.id}
            className={cn(
              'grid gap-3 px-4 py-3 text-sm md:items-center md:gap-4',
              detailDataRowClass,
              customerSubscriptionGridClass,
              getFlowerSubscriptionDetailRowClassName(subscription)
            )}
          >
            <div className="min-w-0">
              <Typography variant="eyebrow" className="block md:hidden">
                Subscription
              </Typography>
              <Link {...path.subscriptions.show(subscription.id).link}>
                <Typography
                  variant="labelStrong"
                  interaction="hoverUnderline"
                  className="block truncate"
                >
                  Subscription #{subscription.id}
                </Typography>
              </Link>
              <Typography variant="supporting" className="mt-1 block">
                {subscription.created_at
                  ? `Started ${formatSubscriptionDate(subscription.created_at)}`
                  : ''}
              </Typography>
            </div>

            <div className="min-w-0">
              <Typography variant="eyebrow" className="block md:hidden">
                Status
              </Typography>
              <div className="flex flex-wrap gap-1.5">
                <SubscriptionStatusBadge subscription={subscription} />
                <SubscriptionSkippedBadge subscription={subscription} />
              </div>
            </div>

            <div className="min-w-0">
              <Typography variant="eyebrow" className="block md:hidden">
                Package
              </Typography>
              <Link {...path.subscriptions.show(subscription.id).link}>
                <Typography
                  variant="labelStrong"
                  interaction="hoverUnderline"
                  className="block truncate"
                >
                  {getSubscriptionName(subscription)}
                </Typography>
              </Link>
              <Typography variant="supporting" className="mt-1 block truncate">
                {getSubscriptionTierName(subscription)} ·{' '}
                {getSubscriptionFrequencyLabel(subscription)}
              </Typography>
            </div>

            <div className="min-w-0 md:text-right">
              <Typography variant="eyebrow" className="block md:hidden">
                Next
              </Typography>
              <span className="block font-medium tabular-nums">
                {formatSubscriptionDate(subscription.next_fulfillment_date)}
              </span>
              <Typography variant="supporting" className="mt-1 block">
                Charge {formatSubscriptionDate(subscription.next_charge_date)}
              </Typography>
            </div>

            <div className="min-w-0 md:text-right">
              <Typography variant="eyebrow" className="block md:hidden">
                Payment
              </Typography>
              <span className="block whitespace-nowrap text-muted-foreground">
                {getSubscriptionPaymentMethodLabel(subscription)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function SubscriptionSummaryWell({
  subscription,
}: {
  subscription?: SubscriptionPackageView | null
}) {
  const path = usePathHelper()
  const productId = subscription?.product?.id
  const productLink = productId ? path.products.show(productId).link : null
  const media = getSubscriptionImageMedia(subscription)
  const name = getSubscriptionName(subscription)
  const tier = getSubscriptionTierName(subscription)
  const frequency = getSubscriptionFrequencyLabel(subscription)
  const amount = formatCurrency(parseCurrency(subscription?.amounts?.item))

  return (
    <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:px-5">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <ImageLightboxPreviewTrigger
          media={media}
          alt={name}
          caption={name}
          frameClassName="aspect-square h-20 w-20 rounded-xl border border-border/60 bg-muted/35 shadow-sm"
          imageClassName="object-cover"
        />

        <div className="min-w-0 flex-1 space-y-1.5">
          <Typography variant="eyebrow">Subscription package</Typography>
          {productLink ? (
            <Link {...productLink}>
              <Typography
                variant="body"
                weight="semibold"
                interaction="hoverUnderline"
                className="block truncate"
              >
                {name}
              </Typography>
            </Link>
          ) : (
            <Typography variant="cardTitle" className="truncate">
              {name}
            </Typography>
          )}
          <Typography variant="supporting" className="block">
            {tier} · {frequency}
          </Typography>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-border/60 pt-3 sm:min-w-[132px] sm:flex-col sm:items-end sm:border-t-0 sm:border-l sm:pl-4 sm:pt-0">
        <div className="text-left sm:text-right">
          <Typography variant="label" tone="muted">
            {frequency}
          </Typography>
          <Typography variant="h5" className="mt-1">
            {amount}
          </Typography>
        </div>

        {productLink ? (
          <Button variant="outline" size="sm" asChild>
            <Link {...productLink}>Package</Link>
          </Button>
        ) : null}
      </div>
    </div>
  )
}
